<template>
  <div class="login-screen">
    <div class="login-card">
      <AppAuthBrand :data="brandData" :logoAlt="companyName" subtitle-size="16px" title-color="#151a24"
        subtitle-color="#64748b" gap="10px" />

      <!-- Set new password -->
      <div>
        <v-form class="form-style">
          <AppTextField v-model="password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password"
            prepend-inner-icon="mdi-lock-outline" :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="New password" class="auth-input" :error-messages="passwordError"
            @update:modelValue="passwordError = ''" @click:append-inner="showPassword = !showPassword" />

          <AppTextField v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
            autocomplete="new-password" prepend-inner-icon="mdi-lock-check-outline" :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="Confirm new password" class="auth-input" :error-messages="confirmPasswordError"
            @update:modelValue="confirmPasswordError = ''" @click:append-inner="showConfirmPassword = !showConfirmPassword" />
        </v-form>

        <div class="mt-2">
          <AppButtonFlat block @click="handleChangePassword" :loading="isResettingPassword" :disabled="isResettingPassword">
            Change Password
          </AppButtonFlat>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import { getContextKind, resetPassword } from "@/modules/auth/services/auth.api";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { useAppStore } from '@/shared/stores/app';
import fallbackLogo from '@/assets/s-logo.png';
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { getActiveContext, getAuthToken, getCustomer, setCustomer } from "@/modules/auth/services/auth.storage";

const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isResettingPassword = ref(false);
const router = useRouter();
const route = useRoute();
const emailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const snackbar = useSnackbarStore();
const appStore = useAppStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim(),
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => "Set a new password to secure your account.");
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

onMounted(() => {
  // Prefer email from stored auth_customer (logged-in flow).
  try {
    const customer: any = getCustomer<any>() || null;
    const storedEmail = typeof customer?.email === "string" ? customer.email.trim() : "";
    if (storedEmail && !email.value) {
      email.value = storedEmail;
    } else if (!storedEmail) {
      snackbar.error("Missing account email. Please log in again.");
      router.replace({ name: "login.page" }).catch(() => {});
      return;
    }
  } catch {
    snackbar.error("Unable to read account data. Please log in again.");
    router.replace({ name: "login.page" }).catch(() => {});
  }
});


async function handleChangePassword() {
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";


  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  if (emailError.value || passwordError.value || confirmPasswordError.value) {
    return;
  }

  try {
    isResettingPassword.value = true;
    const response = await resetPassword({
      email: email.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });

    snackbar.success(response?.message || "Password changed successfully.");

    // Mark first-login flow as completed locally so dashboard middleware doesn't bounce us back.
    try {
      const existing: any = getCustomer<any>() || null;
      if (existing && typeof existing === "object") {
        setCustomer({ ...existing, first_login: false });
        window.dispatchEvent(new Event("auth-changed"));
      }
    } catch {
      // ignore
    }

    // If already logged in, route back to the correct dashboard; otherwise fall back to login.
    try {
      const hasToken = Boolean(getAuthToken());
  
      if (hasToken) {
        const kind = getContextKind(getActiveContext() as any);
        if (kind === "customer") {
          router.replace({ name: "customer.dashboard" }).catch(() => {});
          return;
        }
        if (kind === "vendor") {
          router.replace({ name: "vendor.dashboard" }).catch(() => {});
          return;
        }

        router.replace({ name: "marketplace.home" }).catch(() => {});
        return;
      }
    } catch {
      // ignore
    }

    router.replace({ name: "login.page" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to reset password";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length) {
        confirmPasswordError.value = errors.password_confirmation[0];
      }
    }

    if (!emailError.value && !passwordError.value && !confirmPasswordError.value) {
      snackbar.error(message);
    }
  } finally {
    isResettingPassword.value = false;
  }
}
</script>

<style scoped>
.login-screen {
  height: calc(100vh - 250px);
  display: flex;
  align-items: center;
}

.login-card {
  width: 100%;
  height: calc(100vh - 170px);
  border-radius: 18px;
  padding: 26px 22px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-style {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reset-steps {
  display: flex;
  align-items: center;
  gap: 10px;
}

.reset-steps__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
}

.reset-steps__item.active {
  color: #0f172a;
}

.reset-steps__bubble {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
}

.reset-steps__item.active .reset-steps__bubble {
  border-color: var(--color-theme, #e8612f);
}

.reset-steps__item.done .reset-steps__bubble {
  background: var(--color-theme, #e8612f);
  border-color: var(--color-theme, #e8612f);
  color: #fff;
}

.reset-steps__label {
  font-size: 13px;
  font-weight: 600;
}

.reset-steps__line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  border-radius: 999px;
}

.reset-steps__line.done {
  background: var(--color-theme, #e8612f);
}

.reset-actions {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.reset-actions__btn {
  height: 44px !important;
  font-size: 14px;
}
</style>
